/**
 * Tag creator (CRM chats.js tag-creator): the creators this Account can tag on
 * OnlyFans, OnlyFans' own name search from two characters; a pick puts
 * "@username " in the message.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { LoadingBlock } from '../components/Spinner';
import { rankCreators, searchCreators, type Creator } from './mentions';

interface Props {
  accountId: string;
  onPick: (creator: Creator) => void;
  onClose: () => void;
}

export function TagCreatorPicker({ accountId, onPick, onClose }: Props) {
  const { t } = useTranslation('chat');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Creator[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLInputElement>(null);

  useEffect(() => box.current?.focus(), []);

  useEffect(() => {
    let live = true;
    const q = query.trim().toLowerCase();
    const timer = window.setTimeout(
      () => {
        const base = searchCreators(accountId, '');
        Promise.all([q.length >= 2 ? searchCreators(accountId, q) : Promise.resolve([] as Creator[]), base])
          .then(([searched, first]) => {
            if (!live) return;
            setItems(rankCreators(q, [searched, first], 50));
            setError(null);
          })
          .catch((err: unknown) => live && setError((err as Error).message));
      },
      q.length >= 2 ? 350 : 0,
    );
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [accountId, query]);

  return (
    <div className="rc-pop rc-tag-pop" role="dialog" aria-label={t('tagCreator.title')}>
      <div className="rc-pop-head">
        <FeatherIcon name="search" size={14} />
        <input
          ref={box}
          type="search"
          value={query}
          placeholder={t('tagCreator.search')}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
        />
        <button type="button" className="rc-pop-close" title={t('common.close')} aria-label={t('common.close')} onClick={onClose}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      <div className="rc-tag-list">
        {items == null && !error && <LoadingBlock pad="16px 0" label={t('mention.loading')} />}
        {error && <div className="rc-pop-empty">{error}</div>}
        {items && !items.length && <div className="rc-pop-empty">{t('mention.none')}</div>}
        {items?.map((c) => (
          <button key={c.username} type="button" className="rc-tag-row" onClick={() => onPick(c)}>
            <span className="rc-mention-ini">{(c.name || c.username).slice(0, 1).toUpperCase()}</span>
            <span className="rc-tag-names">
              <b>{c.name}</b>
              <span>@{c.username}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
